import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { GetSerieDetailResponse, GetSerieEconomicsResponse } from '@/api/generated/schemas';
import { cn } from '@/shared/lib/utils';
import { Skeleton } from '@/shared/ui';
import { CapacityBar } from '../../../CapacityBar';
import { formatInteger, formatOneDecimal } from '../serieFormat';
import { toSerieTransportKey } from '../../internal/serieLayers';

interface SerieStatsZoneProps {
  detail: GetSerieDetailResponse;
  economics: GetSerieEconomicsResponse | undefined;
  isEconomicsLoading: boolean;
}

const PILL_SHELL =
  'bg-phx-surface-2 border-phx-border rounded-phx inline-flex h-[22px] items-center gap-[5px] border px-[9px]';
const PILL_KEY = 'text-phx-text-muted text-[9px] font-semibold tracking-[0.05em] uppercase';

function StatPill({
  label,
  children,
  mono,
}: {
  label: string;
  children: ReactNode;
  mono?: boolean;
}) {
  return (
    <span className={PILL_SHELL}>
      <span className={PILL_KEY}>{label}</span>
      <span
        className={cn(
          'text-phx-sm text-phx-text font-semibold tabular-nums',
          mono && 'text-phx-xs font-mono'
        )}
      >
        {children}
      </span>
    </span>
  );
}

/**
 * Horní lišta statistik seriálu (schválená varianta „pilulky“). Ekonomické pilulky (Rezervace / Cena / Marže) ukazují
 * skeleton, dokud nedoběhne samostatný ekonomický request.
 */
export function SerieStatsZone({ detail, economics, isEconomicsLoading }: SerieStatsZoneProps) {
  const { t } = useTranslation('planner');
  const loadingEconomics = isEconomicsLoading || !economics;

  return (
    <div className="flex flex-wrap items-center gap-[6px]">
      <StatPill label={t('serieDetail.stats.code')} mono>
        {detail.productCode}
      </StatPill>
      <StatPill label={t('serieDetail.stats.transport')}>
        {t(`serie.transport.${toSerieTransportKey(detail.transportType)}`)}
      </StatPill>
      <StatPill label={t('serieDetail.stats.legs')}>{detail.legsCount}</StatPill>
      <StatPill label={t('serieDetail.stats.transfers')}>{detail.transfersCount}</StatPill>
      <StatPill label={t('serieDetail.stats.capacity')}>{detail.capacityPerTerm}</StatPill>

      <span className={PILL_SHELL}>
        <span className={PILL_KEY}>{t('serieDetail.stats.reservation')}</span>
        {loadingEconomics ? (
          <Skeleton width={58} />
        ) : (
          <CapacityBar
            used={economics.reservationsBooked}
            capacity={economics.reservationsCapacity}
            width={40}
            showPercent
          />
        )}
      </span>

      <StatPill label={t('serieDetail.stats.price')}>
        {loadingEconomics ? (
          <Skeleton width={56} />
        ) : (
          `${t('serieDetail.priceFrom')} ${formatInteger(economics.priceFrom)} ${economics.currency}`
        )}
      </StatPill>
      <StatPill label={t('serieDetail.stats.margin')}>
        {loadingEconomics ? (
          <Skeleton width={40} />
        ) : (
          `${formatOneDecimal(economics.marginPercent)} %`
        )}
      </StatPill>
      <StatPill label={t('serieDetail.stats.terms')}>{detail.termsCount}</StatPill>
    </div>
  );
}
